import React, { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useParams, useHistory } from 'react-router-dom';
import { RootState, useAppDispatch } from '../../../../store';
import { showFeedbackModal } from '../../../../store/actions/UserFeedbackActions';
import { PiSpinnerGapLight, PiCheckCircleLight, PiWarningCircleLight, PiFileTextLight, PiLightningLight } from 'react-icons/pi';
import { getMultiHubSocketService } from '../../../../utils/MultiHubSocketService';
import apis from '../../../../utils/apis';
import UnifiedDraftsView from './UnifiedDraftsView';
import DraftViewer from './DraftViewer';
import { useLicenseGate } from '../../../../hooks/useLicenseGate';
import { useGenerateSuggestions } from '../../../../hooks/useGenerateSuggestions';

interface AiDraftSuggestion {
  document_type_id: string;
  reason: string;
  priority: 'high' | 'medium' | 'low';
}

interface DraftMetadata {
  draft_type_id: string;
  document_type_id: string;
  name: string;
  status: 'processing' | 'completed' | 'failed' | 'queued';
  generated_at: string;
  word_count: number;
  version: number;
  error_message?: string | null;
}

interface DraftListData {
  drafts: DraftMetadata[];
  last_updated: string;
}

interface DraftsTabContentProps {
  matterData: any;
  collaborators?: any;
  hasDocuments?: boolean;
}

const DraftsTabContent: React.FC<DraftsTabContentProps> = ({ matterData, collaborators, hasDocuments = false }) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const history = useHistory();
  const { matterId, draftId } = useParams<{ matterId: string; draftId?: string }>();
  const userInfo = useSelector((state: RootState) => state.user.userInfo);
  const { checkAiFeatureGate, hasAiFeatureAccess } = useLicenseGate();

  const [generatingDraftIds, setGeneratingDraftIds] = useState<Set<string>>(new Set());

  // URL-based viewing: draftId from route params determines what we're viewing
  const viewingDraftTypeId = draftId || null;
  const [viewingDraftName, setViewingDraftName] = useState<string>('');
  const [viewingVersionId, setViewingVersionId] = useState<string | undefined>(undefined);
  const [processingMessage, setProcessingMessage] = useState<string>('');
  const [processingProgress, setProcessingProgress] = useState<number>(0);

  const correlationIdRef = useRef<Map<string, string>>(new Map());
  const draftGroupRef = useRef<Map<string, string>>(new Map());
  const refetchDraftsRef = useRef<(() => Promise<void>) | null>(null);
  const pubsubHandlerRef = useRef<((...args: any[]) => void) | null>(null);
  const generatingIdsRef = useRef<Set<string>>(new Set());

  // Determine if the matter has been analyzed at least once
  const hasBeenAnalyzed = useMemo(() => {
    return !!(matterData?.last_analyzed_at || matterData?.is_analysis_run);
  }, [matterData?.last_analyzed_at, matterData?.is_analysis_run]);

  // Fetch suggested draft categories from backend
  const [suggestedCategories, setSuggestedCategories] = useState<string[]>([]);
  const [aiSuggestions, setAiSuggestions] = useState<AiDraftSuggestion[] | null>(null);
  const [suggestionsSource, setSuggestionsSource] = useState<'ai' | 'rule_based' | null>(null);

  // Callback to re-fetch suggestions after AI generation completes
  const refetchSuggestions = useCallback(async () => {
    if (!matterData?.uuid) return;
    try {
      const response = await apis.get(`/drafts/suggested/${matterData.uuid}`);
      if (response.status === 200) {
        setSuggestedCategories(response.data.suggested_categories || []);
        const source = response.data.source || null;
        setSuggestionsSource(source);
        if (source === 'ai' && response.data.ai_suggestions) {
          const normalizePriority = (p?: string): AiDraftSuggestion['priority'] =>
            p === 'high' || p === 'medium' || p === 'low' ? p : 'low';
          const normalized: AiDraftSuggestion[] = response.data.ai_suggestions.map(
            (s: { document_type_id: string; reason: string; priority?: string }) => ({
              document_type_id: s.document_type_id,
              reason: s.reason,
              priority: normalizePriority(s.priority),
            })
          );
          setAiSuggestions(normalized);
          setSuggestedCategories([]);
        }
      }
    } catch (err) {
      console.error('[DRAFTS] Failed to refetch suggestions after generation:', err);
    }
  }, [matterData?.uuid]);

  // Use the async suggestion generation hook (queue → AI engine → PubSub)
  const {
    isGenerating: isGeneratingSuggestions,
    progress: suggestionsProgress,
    handleGenerate: handleGenerateSuggestions,
  } = useGenerateSuggestions({
    matterUuid: matterData?.uuid || '',
    suggestionType: 'drafts',
    onComplete: refetchSuggestions,
  });

  const guardedGenerateSuggestions = useCallback(() => {
    if (!hasDocuments) {
      dispatch(showFeedbackModal({ showModal: true, modalType: 'warning', message: t('Upload at least one case file to enable AI generation.'), duration: 3000 }));
      return;
    }
    handleGenerateSuggestions();
  }, [hasDocuments, handleGenerateSuggestions, dispatch, t]);

  // Keep ref in sync with state for use in event handler
  useEffect(() => {
    generatingIdsRef.current = generatingDraftIds;
  }, [generatingDraftIds]);

  // Hydrate generating state from API on mount — detects items that were
  // already processing before this component mounted (e.g., user navigated away and back).
  // Uses a SINGLE fetch (no polling). If processing items are found, reconnects to
  // PubSub so completion events are received via WebSocket instead of polling.
  useEffect(() => {
    if (!matterData?.uuid) return;

    let cancelled = false;

    const hydrateProcessingItems = async () => {
      try {
        const response = await apis.get(`/drafts/list/${matterData.uuid}/merged`);
        if (cancelled) return;

        if (response.status === 200) {
          const documents = response.data.documents || [];
          const processingDocs = documents.filter(
            (doc: any) => doc.status === 'processing' || doc.status === 'queued'
          );

          if (processingDocs.length > 0) {
            const processingIds = processingDocs.map((doc: any) => doc.document_type_id);
            setGeneratingDraftIds(prev => {
              const next = new Set(prev);
              processingIds.forEach((id: string) => next.add(id));
              return next;
            });
            processingIds.forEach((id: string) => generatingIdsRef.current.add(id));

            // Reconnect PubSub for processing items that have a correlation_id (draft_id).
            // The PubSub group format is "drafts-{draft_id}".
            const multiHubService = getMultiHubSocketService();
            await multiHubService.connectToHub('ai_processing');

            for (const doc of processingDocs) {
              const draftId = doc.correlation_id;
              if (draftId) {
                const group = `drafts-${draftId}`;
                correlationIdRef.current.set(doc.document_type_id, draftId);
                draftGroupRef.current.set(doc.document_type_id, group);
                multiHubService.emit('ai_processing', 'join', { group });
              }
            }

            // Register persistent PubSub handler if not already registered
            if (!pubsubHandlerRef.current) {
              const handler = (data: any) => {
                handleDraftPubSubEvent(data);
              };
              pubsubHandlerRef.current = handler;
              multiHubService.on('ai_processing', '*', handler);
            }
          } else {
            // No processing items — clear any stale hydrated IDs
            setGeneratingDraftIds(prev => {
              if (prev.size === 0) return prev;
              const next = new Set(prev);
              for (const id of prev) {
                if (!correlationIdRef.current.has(id)) {
                  next.delete(id);
                }
              }
              return next;
            });
          }
        }
      } catch (error) {
        console.error('[DRAFTS] Failed to hydrate processing status:', error);
      }
    };

    hydrateProcessingItems();

    return () => {
      cancelled = true;
    };
  }, [matterData?.uuid]);

  // Fetch suggested categories from backend (GET only — no auto-generation)
  useEffect(() => {
    if (!matterData?.uuid) return;
    const normalizePriority = (priority?: string): AiDraftSuggestion['priority'] => {
      if (priority === 'high' || priority === 'medium' || priority === 'low') {
        return priority;
      }
      return 'low';
    };
    const fetchSuggested = async () => {
      try {
        const response = await apis.get(`/drafts/suggested/${matterData.uuid}`);
        if (response.status === 200) {
          setSuggestedCategories(response.data.suggested_categories || []);
          const source = response.data.source || null;
          setSuggestionsSource(source);
          // Only populate AI suggestions when actually generated by AI, not rule-based fallbacks
          if (source === 'ai' && response.data.ai_suggestions) {
            const normalizedSuggestions: AiDraftSuggestion[] = response.data.ai_suggestions.map(
              (suggestion: { document_type_id: string; reason: string; priority?: string }) => ({
                document_type_id: suggestion.document_type_id,
                reason: suggestion.reason,
                priority: normalizePriority(suggestion.priority)
              })
            );
            setAiSuggestions(normalizedSuggestions);
          }
          // If no AI suggestions exist, user can click "Generate Suggestions" button
        }
      } catch (error) {
        console.error('[DRAFTS] Failed to fetch suggested categories:', error);
      }
    };
    fetchSuggested();
  }, [matterData?.uuid, matterData?.last_analyzed_at]);

  // Handle draft generation
  const handleGenerateDraft = useCallback(
    async (draftTypeId: string, structureTemplateId?: string) => {
      if (!checkAiFeatureGate('drafts')) return;
      if (!matterData?.uuid) return;
      if (!hasBeenAnalyzed) return; // Require analysis before generating drafts
      if (!hasDocuments) {
        dispatch(showFeedbackModal({ showModal: true, modalType: 'warning', message: t('Upload at least one case file to enable AI generation.'), duration: 3000 }));
        return;
      }

      setGeneratingDraftIds(prev => new Set(prev).add(draftTypeId));
      generatingIdsRef.current.add(draftTypeId);

      try {
        const payload: any = {
          matter_uuid: matterData.uuid,
          draft_type_id: draftTypeId,
        };
        if (structureTemplateId) {
          payload.structure_template_id = structureTemplateId;
        }
        const response = await apis.post('/drafts/generate', payload);

        if (response.status < 200 || response.status >= 300) {
          throw new Error(`API returned error status: ${response.status}`);
        }

        const { draft_id, pubsub } = response.data;
        correlationIdRef.current.set(draftTypeId, draft_id);
        draftGroupRef.current.set(draftTypeId, pubsub.group);

        // Immediately refresh the merged list to show "Processing" status
        if (refetchDraftsRef.current) {
          await refetchDraftsRef.current();
        }

        const multiHubService = getMultiHubSocketService();
        await multiHubService.connectToHub('ai_processing');
        multiHubService.emit('ai_processing', 'join', { group: pubsub.group });

        // Register a single persistent handler (only once)
        if (!pubsubHandlerRef.current) {
          const handler = (data: any) => {
            handleDraftPubSubEvent(data);
          };
          pubsubHandlerRef.current = handler;
          multiHubService.on('ai_processing', '*', handler);
        }

      } catch (error) {
        console.error('[DRAFTS] Failed to initiate draft generation:', error);
        setGeneratingDraftIds(prev => {
          const next = new Set(prev);
          next.delete(draftTypeId);
          return next;
        });
        generatingIdsRef.current.delete(draftTypeId);
        dispatch(
          showFeedbackModal({
            showModal: true,
            message: t('Failed to start draft generation'),
            modalType: 'error',
            duration: 5000
          })
        );
      }
    },
    [matterData?.uuid, dispatch, t, hasBeenAnalyzed]
  );


  // Handle pubsub events - single handler for ALL concurrent generations
  const handleDraftPubSubEvent = useCallback(
    (data: any) => {
      const eventData = Array.isArray(data) ? data[0] : data || {};

      // Extract fields from both top-level and nested data object
      const nestedData = eventData.data || {};
      const eventCorrelationId =
        eventData.correlationId ||
        eventData.correlation_id ||
        eventData.CorrelationID;

      // Find which draftTypeId this event belongs to by matching correlation ID
      let matchedDraftTypeId: string | null = null;
      if (eventCorrelationId) {
        for (const [typeId, corrId] of correlationIdRef.current.entries()) {
          if (corrId === eventCorrelationId) {
            matchedDraftTypeId = typeId;
            break;
          }
        }
      }

      // If we can't match to a known draft, check if it's for any of our active generations
      if (!matchedDraftTypeId && !eventCorrelationId) {
        // Unmatched event with no correlation — skip
        return;
      }
      if (!matchedDraftTypeId) {
        // Has correlation but doesn't match any of ours — skip
        return;
      }

      const status = eventData.status?.toLowerCase() || '';
      const subType = eventData.sub_type || nestedData.sub_type || eventData.event_name || eventData.event_type || '';
      const message = eventData.message || eventData.stepName || '';
      const progressPercent = eventData.progressPercent ?? eventData.progress_percent ?? eventData.progress;

      // Track live progress messages (show for the most recent event)
      if (message && status !== 'completed' && status !== 'failed') {
        setProcessingMessage(message);
      }
      if (progressPercent !== undefined && progressPercent !== null) {
        setProcessingProgress(Number(progressPercent));
      }

      // Handle completion
      if (status === 'completed' || subType === 'draft_completed') {
        if (!generatingIdsRef.current.has(matchedDraftTypeId)) return; // Already handled

        setGeneratingDraftIds(prev => {
          const next = new Set(prev);
          next.delete(matchedDraftTypeId!);
          return next;
        });
        generatingIdsRef.current.delete(matchedDraftTypeId);

        // Clear progress if no more generating
        setTimeout(async () => {
          if (generatingIdsRef.current.size === 0) {
            setProcessingMessage('');
            setProcessingProgress(0);
          }
          if (refetchDraftsRef.current) {
            await refetchDraftsRef.current();
          }
        }, 1500);
      }

      // Handle failure
      if (status === 'failed' || subType === 'draft_failed') {
        const errorMessage = nestedData.error || eventData.error_message || eventData.error || eventData.message || t('Unknown error');

        setGeneratingDraftIds(prev => {
          const next = new Set(prev);
          next.delete(matchedDraftTypeId!);
          return next;
        });
        generatingIdsRef.current.delete(matchedDraftTypeId);

        if (generatingIdsRef.current.size === 0) {
          setProcessingMessage('');
          setProcessingProgress(0);
        }

        dispatch(
          showFeedbackModal({
            showModal: true,
            message: t('Draft generation failed'),
            subMsg: errorMessage,
            modalType: 'error',
            duration: 5000
          })
        );

        // Refetch merged list to show "Failed" status
        setTimeout(async () => {
          if (refetchDraftsRef.current) {
            await refetchDraftsRef.current();
          }
        }, 1000);

        console.error('[DRAFTS] Generation failed:', errorMessage);
      }
    },
    [dispatch, t]
  );

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      const multiHubService = getMultiHubSocketService();
      draftGroupRef.current.forEach((group) => {
        multiHubService.emit('ai_processing', 'leave', { group });
      });
      if (pubsubHandlerRef.current) {
        multiHubService.off('ai_processing', '*', pubsubHandlerRef.current);
        pubsubHandlerRef.current = null;
      }
    };
  }, []);

  const handleViewDraft = (draftTypeId: string, draftName: string, versionId?: string) => {
    if (!checkAiFeatureGate('drafts')) return;
    setViewingDraftName(draftName);
    setViewingVersionId(versionId);
    history.push(`/matters/${matterId}/document/${draftTypeId}${versionId ? `?version=${versionId}` : ''}`);
  };

  const handleCloseViewer = () => {
    setViewingDraftName('');
    setViewingVersionId(undefined);
    history.push(`/matters/${matterId}?tab=Drafts`);
  };

  const handleBackToList = () => {
    setViewingDraftName('');
    setViewingVersionId(undefined);
    history.push(`/matters/${matterId}?tab=Drafts`);
  };

  // Derive the currently-viewed draft's generating state
  const viewingIsGenerating = viewingDraftTypeId ? generatingDraftIds.has(viewingDraftTypeId) : false;

  // Gate: require at least one analysis before showing drafts
  // if (!hasBeenAnalyzed) {
  //   return (
  //     <div className="flex flex-col items-center justify-center py-16 text-center rounded-lg">
  //       <div className="h-14 w-14 rounded-full bg-amber-50 dark:bg-amber-900/20 flex items-center justify-center mb-4">
  //         <PiLightningLight className="h-7 w-7 text-amber-500" />
  //       </div>
  //       <h4 className="text-base font-semibold text-gray-900 dark:text-white mb-2">
  //         {t('Analysis Required')}
  //       </h4>
  //       <p className="text-sm text-gray-600 dark:text-gray-400 max-w-sm">
  //         {t('Run an analysis on this matter before generating drafts. Upload documents and click the "Analyze" button to get started.')}
  //       </p>
  //     </div>
  //   );
  // }

  // If viewing a draft, show the viewer (gate direct URL access)
  if (viewingDraftTypeId && matterData?.uuid) {
    if (!hasAiFeatureAccess('drafts')) {
      // User navigated directly to a document URL without license — redirect back
      history.replace(`/matters/${matterId}?tab=Drafts`);
      return null;
    }
    return (
      <DraftViewer
        key={`${matterData.uuid}-${viewingDraftTypeId}-${viewingVersionId || 'current'}`}
        matterUuid={matterData.uuid}
        draftTypeId={viewingDraftTypeId}
        draftName={viewingDraftName}
        onBack={handleBackToList}
        onRerun={handleGenerateDraft}
        onGenerate={handleGenerateDraft}
        generatingDraftId={viewingIsGenerating ? viewingDraftTypeId : null}
        initialVersionId={viewingVersionId}
        processingMessage={processingMessage}
        processingProgress={processingProgress}
      />
    );
  }

  return (
    <UnifiedDraftsView
      matterUuid={matterData?.uuid}
      generatingDraftId={generatingDraftIds.size > 0 ? Array.from(generatingDraftIds)[generatingDraftIds.size - 1] : null}
      generatingDraftIds={generatingDraftIds}
      onGenerateDraft={handleGenerateDraft}
      onViewDraft={handleViewDraft}
      onRefresh={() => {
        if (refetchDraftsRef.current) {
          refetchDraftsRef.current();
        }
      }}
      onRefreshReady={(refreshFn) => {
        refetchDraftsRef.current = refreshFn;
      }}
      processingMessage={processingMessage}
      processingProgress={processingProgress}
      suggestedCategories={suggestedCategories}
      aiSuggestions={aiSuggestions}
      suggestionsSource={suggestionsSource}
      isGeneratingSuggestions={isGeneratingSuggestions}
      suggestionsProgress={suggestionsProgress}
      onGenerateSuggestions={guardedGenerateSuggestions}
      hasDocuments={hasDocuments}
      aiFeatureLocked={!hasAiFeatureAccess('drafts')}
    />
  );
};

export default DraftsTabContent;
